import axios from 'axios';
// import { getCookie } from '@/utils/cookies';
import { reactive } from 'vue';
import { setPersistedProvider, clearPersistedProvider, getActiveProvider } from './provider';

let instance;
// const CODE_PATH = '/oauth2/authorized';

export const useDeAuth = (options = {}) => {
    if (instance) return instance;

    instance = reactive({
        options,
        loading: true,
        isAuthenticated: false,
        user: {},
        error: null,
        getParams() {
            const { search } = window.location;
            const parsed = new URLSearchParams(search);
            const code = parsed.get('code');
            const state = parsed.get('state');

            return { code, state };
        },
        async refreshToken(token) {
            const clientId = import.meta.env.VITE_DE_CLIENT_ID;
            const API_DOMAIN = instance.options.apiDomain || import.meta.env.VITE_API_URL;

            const reqParams = new URLSearchParams([
                ['client_id', clientId],
                ['grant_type', 'refresh_token'],
                ['refresh_token', token],
            ]);

            let response;

            try {
                response = await axios.post(`${API_DOMAIN}/deproxy/oauth2/token`, reqParams, {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                });
            } catch {
                instance.logout();
            }

            const accessToken = instance.storeTokens(response.data);
            return accessToken;
        },
        getTokenKey(isRefresh) {
            const clientId = import.meta.env.VITE_DE_CLIENT_ID;

            return `DE::${clientId}::${isRefresh ? 'refresh' : ''}`;
        },
        async requestToken(reqParams) {
            const API_DOMAIN = instance.options.apiDomain || import.meta.env.VITE_API_URL;
            let response;

            try {
                response = await axios.post(`${API_DOMAIN}/deproxy/oauth2/token`, reqParams, {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                });
                instance.error = null;
            } catch {
                const errorMessage = 'Unable to retrieve access token, please try again later';
                instance.error = errorMessage;
                throw new Error(errorMessage);
            }

            const accessToken = instance.storeTokens(response.data);
            return accessToken;
        },
        storeTokens(data) {
            const { access_token: accessToken, refresh_token: refreshToken, expires_in: expiresIn } = data;

            const now = new Date();
            const expiry = now.getTime() + expiresIn * 1000;
            const body = JSON.stringify({
                access_token: accessToken,
                expiry,
            });

            const refreshBody = JSON.stringify({
                refresh_token: refreshToken,
            });

            const tokenKey = instance.getTokenKey();
            const refreshTokenKey = instance.getTokenKey(true);

            // store tokens
            localStorage.setItem(tokenKey, body);
            localStorage.setItem(refreshTokenKey, refreshBody);
            localStorage.removeItem('careers-app-auth-error'); // Clear any previous errors
            instance.isAuthenticated = true;
            instance.error = null;

            return accessToken;
        },
        /**
         * Returns true if a reload was triggered (this page instance is on its way out and
         * its caller should stop rather than settling isAuthenticated/loading on stale state),
         * false if there was nothing to exchange and the caller should proceed as normal.
         */
        async getToken() {
            const { code, state } = instance.getParams();

            if (!code || !state) {
                return false;
            }

            const { origin, pathname } = window.location;

            const clientId = import.meta.env.VITE_DE_CLIENT_ID;

            const statePass = instance.checkState(state);
            localStorage.removeItem('careers-app-state');

            if (!statePass) {
                window.history.replaceState({}, document.title, window.location.pathname);
                window.location.reload();
                return true;
            }

            const reqParams = new URLSearchParams([
                ['code', code],
                ['client_id', clientId],
                ['grant_type', 'authorization_code'],
                ['redirect_uri', `${origin}${pathname}`],
            ]);

            try {
                await instance.requestToken(reqParams);
            } catch (e) {
                console.error('[deAuth] Token request failed:', e);
                // Store error in localStorage so it persists across reload
                const errorMessage = e instanceof Error ? e.message : String(e);
                localStorage.setItem('careers-app-auth-error', errorMessage);
                instance.error = errorMessage;
            } finally {
                // Not setting instance.loading = false here: a reload is happening either way,
                // and flipping it now (even briefly, before the reload actually takes effect)
                // is exactly what lets a concurrent waitForAuthLoaded() watcher elsewhere race
                // ahead against this page instance while it's on its way out.
                window.history.replaceState({}, document.title, window.location.pathname);
                // Reload to process authentication state (error or success)
                window.location.reload();
            }

            return true;
        },
        getTokenSilently() {
            const tokenKey = instance.getTokenKey();
            const fromStorage = localStorage.getItem(tokenKey);
            if (!fromStorage) {
                if (instance.isAuthenticated) {
                    instance.logout();
                }
                return null;
            }
            const { access_token: accessToken, expiry } = JSON.parse(fromStorage);

            let now = new Date();

            now = now.getTime();
            // if access token has expired, get a new one
            if (now > expiry) {
                const refreshTokenKey = instance.getTokenKey(true);
                const { refresh_token: refreshToken } = JSON.parse(localStorage.getItem(refreshTokenKey));

                return instance.refreshToken(refreshToken);
            }

            return accessToken;
        },
        getUser() {
            const tokenKey = instance.getTokenKey();
            return localStorage.getItem(tokenKey);
        },
        async checkDEAuthentication() {
            const accessToken = await instance.getUser();
            return !!accessToken;
        },
        checkState(stateRemote) {
            const stateLocal = localStorage.getItem('careers-app-state');
            return stateLocal === stateRemote;
        },
        login() {
            const state = window.crypto.randomUUID();
            const currentDomain = window.location.origin;
            localStorage.setItem('careers-app-state', state);

            const params = new URLSearchParams([
                ['response_type', 'code'],
                ['client_id', options.clientId],
                ['state', state],
                ['redirect_uri', `${currentDomain}/oauth2/authorized`],
                [
                    'scope',
                    'https://api.discoveryeducation.com/scopes/user.readonly https://api.discoveryeducation.com/scopes/asset.readonly https://api.discoveryeducation.com/scopes/onboarding.readonly',
                ],
            ]).toString();

            window.location.href = `${options.deDomain}?${params}`;
        },
        logout() {
            const accessTokenKey = instance.getTokenKey();
            const refreshTokenKey = instance.getTokenKey(true);

            localStorage.removeItem(accessTokenKey);
            localStorage.removeItem(refreshTokenKey);

            instance.isAuthenticated = false;
            clearPersistedProvider();
            window.location.reload();
        },
    });

    // Initialize (replaces created() lifecycle hook)

    (async () => {
        try {
            // Check for persisted error from previous session
            const persistedError = localStorage.getItem('careers-app-auth-error');
            if (persistedError) {
                instance.error = persistedError;
                localStorage.removeItem('careers-app-auth-error');
            }

            // We can verify a redirect/full-page-refresh because the active
            // provider would still be set.
            if (getActiveProvider() === 'de') {
                const reloadTriggered = await instance.getToken();
                if (reloadTriggered) return;
            }
        } catch (e) {
            console.error('[deAuth] Initialization error:', e);
            instance.error = e;
        }
        instance.isAuthenticated = await instance.checkDEAuthentication();
        if (instance.isAuthenticated) {
            setPersistedProvider('de');
        }
        instance.loading = false;
    })();

    return instance;
};

export const getInstance = () => instance;
